import { useMemo, useState } from 'react';
import {
  CANVAS_PRESETS,
  getCanvasItemBounds,
  getCanvasWarnings,
  getProjectionBlendZones,
  renameCanvasItem,
} from '../canvas';
import { MAX_CANVAS_DIMENSION, MAX_CANVAS_PIXELS } from '../config';
import type { CanvasDocument, CanvasRotation, GeneratorConfig, StoredProject } from '../types';
import { CollapsibleControlSection } from './CollapsibleControlSection';

interface CanvasControlsProps {
  canvas: CanvasDocument;
  selectedIds: string[];
  currentConfig: GeneratorConfig;
  savedProjects: StoredProject[];
  updateCanvas: (updater: (current: CanvasDocument) => CanvasDocument) => void;
  onSelect: (id: string | null, additive?: boolean) => void;
  onDistributeSelection: (axis: 'horizontal' | 'vertical') => void;
  onAddCurrent: () => void;
  onAddSaved: (projectId: string) => void;
  onNewScreen: () => void;
  onEditScreen: (id: string) => void;
  onCopyScreen: (id: string) => void;
  onPasteScreen: () => void;
  onDuplicateScreen: (id: string) => void;
  canPaste: boolean;
  onDeleteScreen: (id: string) => void;
}

type CanvasControlSectionId = 'setup' | 'items' | 'selection' | 'blend' | 'annotations' | 'help';

export function CanvasControls({
  canvas,
  selectedIds,
  currentConfig,
  savedProjects,
  updateCanvas,
  onSelect,
  onDistributeSelection,
  onAddCurrent,
  onAddSaved,
  onNewScreen,
  onEditScreen,
  onCopyScreen,
  onPasteScreen,
  onDuplicateScreen,
  canPaste,
  onDeleteScreen,
}: CanvasControlsProps) {
  const [savedProjectId, setSavedProjectId] = useState('');
  const [renamingItemId, setRenamingItemId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [openSections, setOpenSections] = useState<Set<CanvasControlSectionId>>(
    () => new Set(['items', 'selection']),
  );
  const warnings = useMemo(() => getCanvasWarnings(canvas), [canvas]);
  const blendZones = useMemo(() => getProjectionBlendZones(canvas), [canvas]);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedItems = canvas.items.filter((item) => selectedIdSet.has(item.id));
  const selected = selectedItems.length === 1 ? selectedItems[0]! : null;
  const clipboardSelected =
    canvas.items.find((item) => item.id === selectedIds.at(-1)) ?? selectedItems.at(-1) ?? null;
  const selectedBounds = selected ? getCanvasItemBounds(selected) : null;
  const compatibleProjects = savedProjects.filter(
    (project) => project.config.mode === canvas.canvasType,
  );
  const itemLabel = canvas.canvasType === 'led' ? 'LED screen' : 'projection';
  const presetValue =
    CANVAS_PRESETS.find(
      (preset) =>
        preset.width === canvas.resolution.width && preset.height === canvas.resolution.height,
    )?.label ?? 'Custom';
  const maximumWidth = Math.min(
    MAX_CANVAS_DIMENSION,
    Math.floor(MAX_CANVAS_PIXELS / canvas.resolution.height),
  );
  const maximumHeight = Math.min(
    MAX_CANVAS_DIMENSION,
    Math.floor(MAX_CANVAS_PIXELS / canvas.resolution.width),
  );
  const enabledAnnotationCount = Object.values(canvas.annotations).filter(Boolean).length;

  const toggleSection = (sectionId: CanvasControlSectionId) => {
    setOpenSections((current) => {
      const next = new Set(current);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const updateSelected = (patch: Partial<NonNullable<typeof selected>>) => {
    if (!selected) return;
    updateCanvas((current) => ({
      ...current,
      items: current.items.map((item) => (item.id === selected.id ? { ...item, ...patch } : item)),
    }));
  };

  const startRename = (id: string, name: string) => {
    onSelect(id, false);
    setRenamingItemId(id);
    setRenameDraft(name);
  };

  const cancelRename = () => {
    setRenamingItemId(null);
    setRenameDraft('');
  };

  const commitRename = (id: string) => {
    updateCanvas((current) => ({
      ...current,
      items: current.items.map((item) =>
        item.id === id ? renameCanvasItem(item, renameDraft) : item,
      ),
    }));
    cancelRename();
  };

  const moveLayer = (direction: 'front' | 'back') => {
    if (!selected) return;
    updateCanvas((current) => {
      const item = current.items.find((candidate) => candidate.id === selected.id);
      if (!item) return current;
      const remaining = current.items.filter((candidate) => candidate.id !== selected.id);
      return {
        ...current,
        items: direction === 'front' ? [...remaining, item] : [item, ...remaining],
      };
    });
  };

  return (
    <div className="controls canvas-controls" aria-label="Canvas settings">
      <CollapsibleControlSection
        title="Canvas setup"
        summary={`${canvas.canvasType === 'led' ? 'LED' : 'Projection'} · ${canvas.resolution.width} × ${canvas.resolution.height}`}
        badge={canvas.canvasType === 'led' ? 'LED' : 'Projection'}
        isOpen={openSections.has('setup')}
        onToggle={() => toggleSection('setup')}
        intro
      >
        <div className="canvas-type-context-note">
          <span>Canvas type</span>
          <strong>{canvas.canvasType === 'led' ? 'LED' : 'Projection'}</strong>
          <small>Change it in the top menu.</small>
        </div>
        <label className="field">
          <span>Canvas name</span>
          <input
            value={canvas.title}
            maxLength={120}
            onChange={(event) =>
              updateCanvas((current) => ({ ...current, title: event.target.value }))
            }
          />
        </label>
        <label className="field">
          <span>Resolution preset</span>
          <select
            value={presetValue}
            onChange={(event) => {
              const preset = CANVAS_PRESETS.find((item) => item.label === event.target.value);
              if (!preset || preset.width === 0) return;
              updateCanvas((current) => ({
                ...current,
                resolution: { width: preset.width, height: preset.height },
              }));
            }}
          >
            {CANVAS_PRESETS.map((preset) => (
              <option key={preset.label} value={preset.label}>
                {preset.label}
              </option>
            ))}
          </select>
        </label>
        <div className="field-row">
          <CanvasNumberField
            label="Width"
            value={canvas.resolution.width}
            min={1}
            max={maximumWidth}
            suffix="px"
            onChange={(width) =>
              updateCanvas((current) => ({
                ...current,
                resolution: { ...current.resolution, width },
              }))
            }
          />
          <CanvasNumberField
            label="Height"
            value={canvas.resolution.height}
            min={1}
            max={maximumHeight}
            suffix="px"
            onChange={(height) =>
              updateCanvas((current) => ({
                ...current,
                resolution: { ...current.resolution, height },
              }))
            }
          />
        </div>
        <label className="field">
          <span>Empty area</span>
          <select
            value={canvas.background}
            onChange={(event) =>
              updateCanvas((current) => ({
                ...current,
                background: event.target.value === 'transparent' ? 'transparent' : 'black',
              }))
            }
          >
            <option value="black">Black</option>
            <option value="transparent">Transparent</option>
          </select>
        </label>
      </CollapsibleControlSection>

      <CollapsibleControlSection
        title={canvas.canvasType === 'led' ? 'LED screens' : 'Projections'}
        summary={`${canvas.items.length} item${canvas.items.length === 1 ? '' : 's'} on Canvas`}
        badge={canvas.items.length}
        isOpen={openSections.has('items')}
        onToggle={() => toggleSection('items')}
      >
        <div className="canvas-add-actions">
          <button
            type="button"
            className="button button--primary"
            disabled={currentConfig.mode !== canvas.canvasType || canvas.items.length >= 200}
            title={
              currentConfig.mode !== canvas.canvasType
                ? `Switch the generator to ${canvas.canvasType} first`
                : ''
            }
            onClick={onAddCurrent}
          >
            Add current {itemLabel}
          </button>
          <button
            type="button"
            className="button button--ghost"
            disabled={canvas.items.length >= 200}
            onClick={onNewScreen}
          >
            New {itemLabel}
          </button>
        </div>
        <div className="canvas-saved-add">
          <select
            value={savedProjectId}
            aria-label={`Saved ${itemLabel}`}
            onChange={(event) => setSavedProjectId(event.target.value)}
          >
            <option value="">Saved {itemLabel}…</option>
            {compatibleProjects.map((project) => (
              <option value={project.id} key={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="button button--ghost"
            disabled={!savedProjectId || canvas.items.length >= 200}
            onClick={() => {
              onAddSaved(savedProjectId);
              setSavedProjectId('');
            }}
          >
            Add
          </button>
        </div>
        <div className="canvas-clipboard-actions">
          <button
            type="button"
            className="button button--ghost"
            disabled={!clipboardSelected}
            title="Copy selected item (Ctrl+C or Cmd+C)"
            onClick={() => clipboardSelected && onCopyScreen(clipboardSelected.id)}
          >
            Copy
          </button>
          <button
            type="button"
            className="button button--ghost"
            disabled={!canPaste}
            title="Paste copied item (Ctrl+V or Cmd+V)"
            onClick={onPasteScreen}
          >
            Paste
          </button>
          <span>Ctrl/Cmd+C · Ctrl/Cmd+V</span>
        </div>
        {canvas.items.length === 0 ? (
          <p className="control-hint">Add a {itemLabel}, then drag it into position.</p>
        ) : (
          <div className="canvas-screen-list" aria-label="Canvas items">
            {[...canvas.items].reverse().map((item) => {
              const hasOverlap = warnings.overlapIds.has(item.id);
              const isOutside = warnings.outsideIds.has(item.id);
              return (
                <div
                  key={item.id}
                  className={`canvas-screen-row ${
                    canvas.canvasType === 'projection' ? 'allows-multi-select' : ''
                  } ${selectedIdSet.has(item.id) ? 'is-selected' : ''}`}
                >
                  {canvas.canvasType === 'projection' && (
                    <label className="canvas-screen-check" title="Add to distribution selection">
                      <input
                        type="checkbox"
                        checked={selectedIdSet.has(item.id)}
                        aria-label={`Select ${item.name} for distribution`}
                        onChange={() => onSelect(item.id, true)}
                      />
                    </label>
                  )}
                  {renamingItemId === item.id ? (
                    <div className="canvas-screen-rename-form">
                      <input
                        autoFocus
                        value={renameDraft}
                        maxLength={120}
                        aria-label={`New name for ${item.name}`}
                        onChange={(event) => setRenameDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') commitRename(item.id);
                          if (event.key === 'Escape') cancelRename();
                        }}
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="canvas-screen-select"
                      onClick={(event) =>
                        onSelect(item.id, event.ctrlKey || event.metaKey || event.shiftKey)
                      }
                    >
                      <span>{item.name}</span>
                      <small>
                        X {item.x} · Y {item.y} · {item.rotation}°
                      </small>
                      {(isOutside || (canvas.canvasType === 'led' && hasOverlap)) && (
                        <strong title="This item needs attention">⚠</strong>
                      )}
                    </button>
                  )}
                  <div className="canvas-screen-actions">
                    {renamingItemId === item.id ? (
                      <>
                        <button
                          type="button"
                          className="canvas-screen-edit"
                          onClick={() => commitRename(item.id)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="canvas-screen-edit canvas-screen-cancel"
                          onClick={cancelRename}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="canvas-screen-edit"
                          aria-label={`Rename ${item.name}`}
                          onClick={() => startRename(item.id, item.name)}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="canvas-screen-edit"
                          aria-label={`Edit ${item.name}`}
                          onClick={() => {
                            onSelect(item.id, false);
                            onEditScreen(item.id);
                          }}
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleControlSection>

      {canvas.canvasType === 'projection' && selectedItems.length >= 2 && (
        <CollapsibleControlSection
          title="Selected projections"
          summary={`${selectedItems.length} projections ready to distribute`}
          badge={selectedItems.length}
          isOpen={openSections.has('selection')}
          onToggle={() => toggleSection('selection')}
          className="canvas-selection canvas-multi-selection"
        >
          <p className="control-hint">
            Fill the Canvas along one axis. Adjacent blend zones are equalized using whole pixels;
            the other axis is unchanged.
          </p>
          <div className="canvas-distribution-actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => onDistributeSelection('horizontal')}
            >
              ↔ Distribute horizontally
            </button>
            <button
              type="button"
              className="button button--primary"
              onClick={() => onDistributeSelection('vertical')}
            >
              ↕ Distribute vertically
            </button>
          </div>
          <button
            type="button"
            className="button button--ghost canvas-clear-selection"
            onClick={() => onSelect(null)}
          >
            Clear selection
          </button>
        </CollapsibleControlSection>
      )}

      {canvas.canvasType === 'projection' && (
        <CollapsibleControlSection
          title="Blend zones"
          summary={
            blendZones.length === 0
              ? 'No projection overlap'
              : `${blendZones.length} measured zone${blendZones.length === 1 ? '' : 's'}`
          }
          badge={blendZones.length}
          badgeClassName="badge--blend"
          isOpen={openSections.has('blend')}
          onToggle={() => toggleSection('blend')}
          className="canvas-blend-zones"
        >
          {blendZones.length === 0 ? (
            <p className="control-hint">
              Overlap two or more projections to measure and preview their blend zones.
            </p>
          ) : (
            <div className="canvas-blend-list" aria-label="Projection blend zones">
              {blendZones.map((zone, index) => (
                <div className="canvas-blend-row" key={zone.id}>
                  <div>
                    <span className="canvas-blend-swatch" aria-hidden="true" />
                    <strong>Blend {index + 1}</strong>
                    <small>
                      {zone.itemIds.length} projector{zone.itemIds.length === 1 ? '' : 's'}
                    </small>
                  </div>
                  <span title={zone.itemNames.join(' + ')}>{zone.itemNames.join(' + ')}</span>
                  <small>
                    {zone.width} × {zone.height} px
                  </small>
                  <small>
                    X {zone.x} · Y {zone.y}
                  </small>
                </div>
              ))}
            </div>
          )}
        </CollapsibleControlSection>
      )}

      {selected && selectedBounds && (
        <CollapsibleControlSection
          title={`Selected ${itemLabel}`}
          summary={`${selected.name} · X ${selected.x} · Y ${selected.y} · ${selected.rotation}°`}
          badge={
            warnings.outsideIds.has(selected.id) ||
            (canvas.canvasType === 'led' && warnings.overlapIds.has(selected.id))
              ? 'Warning'
              : undefined
          }
          badgeClassName="badge--warning"
          isOpen={openSections.has('selection')}
          onToggle={() => toggleSection('selection')}
          className="canvas-selection"
        >
          <CanvasItemNameField
            key={`${selected.id}:${selected.name}`}
            value={selected.name}
            onCommit={(nameDraft) =>
              updateCanvas((current) => ({
                ...current,
                items: current.items.map((item) =>
                  item.id === selected.id ? renameCanvasItem(item, nameDraft) : item,
                ),
              }))
            }
          />
          <div className="control-subheading canvas-position-heading">Position</div>
          <div className="field-row">
            <CanvasNumberField
              label="X"
              value={selected.x}
              suffix="px"
              onChange={(x) => updateSelected({ x })}
            />
            <CanvasNumberField
              label="Y"
              value={selected.y}
              suffix="px"
              onChange={(y) => updateSelected({ y })}
            />
          </div>
          <label className="field">
            <span>Rotation</span>
            <select
              value={selected.rotation}
              onChange={(event) =>
                updateSelected({ rotation: Number(event.target.value) as CanvasRotation })
              }
            >
              <option value={0}>0°</option>
              <option value={90}>90°</option>
              <option value={180}>180°</option>
              <option value={270}>270°</option>
            </select>
          </label>
          <div className="canvas-rotation-actions">
            <button
              type="button"
              className="button button--ghost"
              onClick={() => updateSelected({ rotation: previousRotation(selected.rotation) })}
            >
              ↶ Rotate left
            </button>
            <button
              type="button"
              className="button button--ghost"
              onClick={() => updateSelected({ rotation: nextRotation(selected.rotation) })}
            >
              Rotate right ↷
            </button>
          </div>
          <div className="canvas-object-stats">
            <span>Occupied size</span>
            <strong>
              {selectedBounds.width} × {selectedBounds.height} px
            </strong>
          </div>
          {warnings.overlapIds.has(selected.id) && (
            <p className="canvas-warning">
              {canvas.canvasType === 'projection'
                ? 'This projection overlaps another projection. Overlap is allowed and follows layer order.'
                : 'This screen overlaps another screen. Export is allowed.'}
            </p>
          )}
          {warnings.outsideIds.has(selected.id) && (
            <p className="canvas-warning">
              This item extends outside the Canvas and will be clipped on export.
            </p>
          )}
          <button
            type="button"
            className="button button--primary canvas-edit-screen"
            onClick={() => onEditScreen(selected.id)}
          >
            Edit {canvas.canvasType === 'led' ? 'pattern' : 'projection'}
          </button>
          <div className="canvas-secondary-actions">
            <button
              type="button"
              className="button button--ghost"
              disabled={canvas.items.length >= 200}
              title="Duplicate selected item (Ctrl+D or Cmd+D)"
              onClick={() => onDuplicateScreen(selected.id)}
            >
              Duplicate · Ctrl/Cmd+D
            </button>
            <button
              type="button"
              className="button button--ghost"
              onClick={() => moveLayer('front')}
            >
              To front
            </button>
            <button
              type="button"
              className="button button--ghost"
              onClick={() => moveLayer('back')}
            >
              To back
            </button>
            <button
              type="button"
              className="button button--ghost button--danger"
              onClick={() => onDeleteScreen(selected.id)}
            >
              Delete
            </button>
          </div>
        </CollapsibleControlSection>
      )}

      <CollapsibleControlSection
        title="Export annotations"
        summary={`${enabledAnnotationCount} of 3 enabled`}
        badge={enabledAnnotationCount}
        isOpen={openSections.has('annotations')}
        onToggle={() => toggleSection('annotations')}
      >
        <div className="toggle-grid">
          <CanvasToggle
            label="Item name"
            checked={canvas.annotations.showName}
            onChange={(showName) =>
              updateCanvas((current) => ({
                ...current,
                annotations: { ...current.annotations, showName },
              }))
            }
          />
          <CanvasToggle
            label="X / Y coordinates"
            checked={canvas.annotations.showCoordinates}
            onChange={(showCoordinates) =>
              updateCanvas((current) => ({
                ...current,
                annotations: { ...current.annotations, showCoordinates },
              }))
            }
          />
          <CanvasToggle
            label="Native resolution"
            checked={canvas.annotations.showResolution}
            onChange={(showResolution) =>
              updateCanvas((current) => ({
                ...current,
                annotations: { ...current.annotations, showResolution },
              }))
            }
          />
        </div>
      </CollapsibleControlSection>

      <CollapsibleControlSection
        title="Positioning"
        summary="Shortcuts, snapping and overlap"
        isOpen={openSections.has('help')}
        onToggle={() => toggleSection('help')}
        className="canvas-help"
      >
        <p className="control-hint">
          Drag items to move them. Edges snap to the Canvas and other items. Hold Alt to disable
          snapping. Arrow keys move 1 px; Shift + arrows move 10 px.
        </p>
        <p className="control-hint">
          Export annotations are configurable above. LED power cables are never copied into Canvas
          items. Projection overlap is allowed and follows layer order.
        </p>
      </CollapsibleControlSection>
    </div>
  );
}

function CanvasItemNameField({
  value,
  onCommit,
}: {
  value: string;
  onCommit: (value: string) => void;
}) {
  return (
    <label className="field">
      <span>Name</span>
      <input
        defaultValue={value}
        maxLength={120}
        onBlur={(event) => onCommit(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
      />
    </label>
  );
}

function CanvasToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="toggle">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="toggle-track" aria-hidden="true" />
      <span>{label}</span>
    </label>
  );
}

interface CanvasNumberFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  suffix?: string;
}

function CanvasNumberField({
  label,
  value,
  onChange,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  suffix,
}: CanvasNumberFieldProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const displayedValue = draft ?? String(value);

  const commit = () => {
    const parsed = Number(displayedValue);
    if (Number.isSafeInteger(parsed) && parsed >= min && parsed <= max) onChange(parsed);
    setDraft(null);
  };

  return (
    <label className="field">
      <span>{label}</span>
      <span className="input-with-suffix">
        <input
          type="number"
          value={displayedValue}
          min={min}
          max={max}
          step={1}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
            if (event.key === 'Escape') setDraft(null);
          }}
        />
        {suffix && <small>{suffix}</small>}
      </span>
    </label>
  );
}

function nextRotation(rotation: CanvasRotation): CanvasRotation {
  return rotation === 270 ? 0 : ((rotation + 90) as CanvasRotation);
}

function previousRotation(rotation: CanvasRotation): CanvasRotation {
  return rotation === 0 ? 270 : ((rotation - 90) as CanvasRotation);
}
